import { z } from 'zod';

/**
 * Catalog of feature flags. New functions ship behind a flag until released (CLAUDE.md, Betrieb).
 * Remove a flag (and its checks) once the feature is released for everyone.
 */
export const featureFlagCatalog = {
  projects: 'Projekte: Übersicht, Projektseiten und Einstellungen (Phase 3).',
  tasks: 'Tasks: Liste, Detailansicht, Verschieben und Papierkorb (Phase 3b).',
  board: 'Kanban-Board pro Projekt (Phase 4).',
  collaboration: 'Zusammenarbeit: Kommentare, Aktivität, Anhänge und Benachrichtigungen (Phase 5).',
  resources: 'Ressourcenplanung: Stammdaten, Abwesenheiten, Buchungen und Auslastung (Phase 7).',
  dashboard: 'Rollenbezogene Übersicht als Startseite (DASH-01..06).',
  gantt: 'Gantt pro Projekt mit Abhängigkeiten und Terminplanung (Phase 6).',
  calendar: 'Kalenderansicht pro Projekt: Tasks am Fälligkeitstag, Monat und Woche (TASK-10).',
  assistant: 'KI-Assistent für Interne (Phase KI, ADR-015).',
  audit_log: 'Audit-Log in der Administration mit Filtern und CSV-Export (ADM-05).',
  system_overview: 'Systemübersicht für Admins: Kennzahlen, Betrieb, Hinweise (ADM-06).',
  project_templates: 'Projektvorlagen: speichern, anwenden, verwalten (PROJ-06..08).',
  bookable_capacity:
    'Buchbarer Anteil der Arbeitszeit pro Person (RES-13); aus = volle Arbeitszeit buchbar.',
  project_invitations: 'Projektleitung lädt Kunden direkt ins Projekt ein (AUTH-03, E-33).',
  dark_mode: 'Darstellung hell, dunkel oder wie das System, pro Konto (NFR-06, E-34).',
  task_overbooking_warning:
    'Hinweis auf Überbuchung der Zuständigen beim Anlegen und Bearbeiten von Tasks (RES-14, E-35).',
  skills_and_areas:
    'Bereiche fachlicher Rollen, Skills von Personen, Filter und „Wer kann was?“ (RES-15, RES-16, E-38).',
  avatars: 'Profilbilder hochladen und anzeigen (ADM-07, E-40); braucht den S3-Speicher.',
  project_reports:
    'Projektstatusberichte für Meetings und Kunden, mit Druckansicht (REP-01..06, E-41).',
} as const satisfies Record<string, string>;

/**
 * Removed flags that may still be set in a server `.env`. They are ignored instead of failing the
 * startup, so a deploy does not depend on cleaning up the configuration first.
 * `microsoft_login`: replaced by the ENTRA_* configuration (phase 1b).
 */
export const retiredFeatureFlags: readonly string[] = ['microsoft_login'];

export type FeatureFlagName = keyof typeof featureFlagCatalog;

export const featureFlagNames = Object.keys(featureFlagCatalog) as [
  FeatureFlagName,
  ...FeatureFlagName[],
]; // Object.keys loses the literal key type; the catalog is non-empty by construction.

/** `admin`: visible only to administrators (pilot in production). */
export const featureFlagStateSchema = z.enum(['off', 'admin', 'on']);
export type FeatureFlagState = z.infer<typeof featureFlagStateSchema>;

export type FeatureFlagStates = Record<FeatureFlagName, FeatureFlagState>;

function isFeatureFlagName(name: string): name is FeatureFlagName {
  return Object.hasOwn(featureFlagCatalog, name);
}

/**
 * Parses `name=state,name=state`. Unknown names or states are errors so that typos in the
 * production configuration fail at startup instead of silently disabling a feature.
 */
export function parseFeatureFlags(
  raw: string,
): { ok: true; states: FeatureFlagStates } | { ok: false; errors: string[] } {
  const states = Object.fromEntries(featureFlagNames.map((name) => [name, 'off'])) as Record<
    FeatureFlagName,
    FeatureFlagState
  >; // filled for every catalog name above
  const errors: string[] = [];

  for (const entry of raw.split(',').map((part) => part.trim())) {
    if (entry === '') continue;
    const [name = '', state = '', ...rest] = entry.split('=').map((part) => part.trim());
    const parsedState = featureFlagStateSchema.safeParse(state);
    if (retiredFeatureFlags.includes(name)) {
      continue;
    } else if (!isFeatureFlagName(name)) {
      errors.push(`unknown feature flag "${name}"`);
    } else if (rest.length > 0 || !parsedState.success) {
      errors.push(`invalid entry "${entry}" (expected ${name}=off|admin|on)`);
    } else {
      states[name] = parsedState.data;
    }
  }

  return errors.length > 0 ? { ok: false, errors } : { ok: true, states };
}

export function isFeatureEnabled(state: FeatureFlagState, viewer: { isAdmin: boolean }): boolean {
  return state === 'on' || (state === 'admin' && viewer.isAdmin);
}

export const featuresResponseSchema = z.object({
  flags: z.record(z.enum(featureFlagNames), z.boolean()),
});
export type FeaturesResponse = z.infer<typeof featuresResponseSchema>;
